import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { ArrowLeftIcon, ArrowDownTrayIcon, ArrowPathIcon, ListBulletIcon } from '@heroicons/react/20/solid'
import { Select } from 'components/select'
import { Divider } from 'components/divider'
import loadingImage from '../../assets/images/boat-loading.gif'
import Flatpickr from 'react-flatpickr'
import { useLazyQuery } from '@apollo/client'
import { GET_CUSTOMER_FEEDBACK_REPORT, SEARCH_LOCATION } from 'graphql/reports/queries'
import moment from 'moment'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import { Badge } from 'components'

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50]

const INITIAL_FILTER_STATE = {
    travel_date: {
        start_date: moment().subtract(6, 'months').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
    },
    station_ids: null,
    action_taken: null,
}

export default function CustomerFeedbackReport() {
    const { t } = useTranslation()
    const navigate = useNavigate()

    // States
    const [filterObj, setFilterObj] = useState(INITIAL_FILTER_STATE)
    const [data, setData] = useState([])
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(10)
    const [currentPage, setCurrentPage] = useState(1)
    const [exportLoading, setExportLoading] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [locations, setLocations] = useState([])
    const [filterApplied, setFilterApplied] = useState(false)

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setData([])
            setFilterApplied(false)
        }
    }, [])

    // GraphQL queries
    const [getCustomerFeedbackReport, { loading: reportLoading }] = useLazyQuery(GET_CUSTOMER_FEEDBACK_REPORT, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.customerFeedbackReport.success && data.customerFeedbackReport.code === '200') {
                setData(data.customerFeedbackReport.feedback_report.map(formatFeedbackData))
                setTotalRows(data.customerFeedbackReport.total_count)
            } else {
                handleError(data.customerFeedbackReport.message)
            }
        },
        onError: error => handleError(error.message),
    })

    const [searchLocation] = useLazyQuery(SEARCH_LOCATION, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.searchLocation.success && data.searchLocation.code === '200') {
                setLocations(data.searchLocation.locations)
            }
        },
        onError: error => handleError(error.message),
    })

    // Separate function for fetching data for export
    const [getCustomerFeedbackReportForExport] = useLazyQuery(GET_CUSTOMER_FEEDBACK_REPORT, {
        fetchPolicy: 'network-only',
        onError: error => {
            handleError(error.message)
            setExportLoading(false)
        },
    })

    // Helper Functions
    const formatFeedbackData = feedback => ({
        feedbackDate: feedback.feedback_date,
        travelDate: feedback.travel_date,
        resolvedDate: feedback.resolved_date,
        status: feedback.resolution_status,
        rating: feedback.rating,
    })

    const handleError = message => {
        setDialogMessage(message || t('Failed to fetch customer feedback report'))
        setShowErrDialog(true)
    }

    const fetchBatchForExport = async (offset, limit, filterData = filterObj) => {
        try {
            const result = await getCustomerFeedbackReportForExport({
                variables: {
                    page: {
                        limit,
                        offset,
                    },
                    feedbackFilter: {
                        travel_date: {
                            start_date:
                                filterData.travel_date.start_date ||
                                moment().subtract(6, 'months').format('YYYY-MM-DD'),
                            end_date: filterData.travel_date.end_date || moment().format('YYYY-MM-DD'),
                        },
                        station_ids: filterData.station_ids || null,
                        action_taken: filterData.action_taken,
                    },
                },
            })

            if (result.data?.customerFeedbackReport?.success && result.data.customerFeedbackReport.code === '200') {
                return {
                    data: result.data.customerFeedbackReport.feedback_report.map(formatFeedbackData),
                    total: result.data.customerFeedbackReport.total_count,
                }
            } else {
                throw new Error(result.data?.customerFeedbackReport?.message || 'Failed to fetch data')
            }
        } catch (error) {
            throw error
        }
    }

    const handleExportCSV = async () => {
        try {
            setExportLoading(true)

            // Create a copy of the current filterObj state
            const currentFilterObj = { ...filterObj }

            // Get initial batch to get total count
            const initialBatch = await fetchBatchForExport(0, 100, currentFilterObj)
            const totalRecords = initialBatch.total

            if (totalRecords === 0) {
                handleError(t('No records found to export'))
                return
            }

            let allData = [...initialBatch.data]
            let offset = 100

            // Continue fetching if there are more records
            while (allData.length < totalRecords) {
                const batchData = await fetchBatchForExport(offset, 100, currentFilterObj)
                allData = [...allData, ...batchData.data]
                offset += 100
            }

            // Generate CSV
            const headers = ['Feedback Date', 'Travel Date', 'Resolved Date', 'Status', 'Rating']

            const csvContent = [
                headers.join(','),
                ...allData.map(row =>
                    [
                        row.feedbackDate ? moment(row.feedbackDate).format('YYYY-MM-DD') : 'N/A',
                        row.travelDate ? moment(row.travelDate).format('YYYY-MM-DD') : 'N/A',
                        row.resolvedDate ? moment(row.resolvedDate).format('YYYY-MM-DD') : 'N/A',
                        row.status,
                        row.rating?.toFixed(1),
                    ].join(',')
                ),
            ].join('\n')

            // Create and download file
            const blob = new Blob([csvContent], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `customer-feedback-report-${moment().format('YYYY-MM-DD')}.csv`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            handleError(error.message)
        } finally {
            setExportLoading(false)
        }
    }

    // Table columns configuration
    const columns = useMemo(
        () => [
            {
                name: t('Feedback Date'),
                selector: row => row.feedbackDate,
                sortable: false,
                className: 'font-bold',
                cell: row => <p>{row.feedbackDate ? moment(row.feedbackDate).format('DD MMM YYYY') : 'N/A'}</p>,
            },
            {
                name: t('Travel Date'),
                selector: row => row.travelDate,
                sortable: false,
                className: 'font-bold',
                cell: row => <p>{row.travelDate ? moment(row.travelDate).format('DD MMM YYYY') : 'N/A'}</p>,
            },
            {
                name: t('Resolved Date'),
                selector: row => row.resolvedDate,
                sortable: false,
                className: 'font-bold',
                cell: row => <p>{row.resolvedDate ? moment(row.resolvedDate).format('DD MMM YYYY') : 'N/A'}</p>,
            },
            {
                name: t('Status'),
                selector: row => row.status,
                sortable: false,
                className: 'font-bold',
                cell: row => (
                    <Badge color={`${row.status == 'resolved' ? 'lime' : 'red'}`}>{row.status.toUpperCase()}</Badge>
                ),
            },
            {
                name: t('Rating'),
                selector: row => row.rating?.toFixed(1),
                sortable: false,
                cell: row => <p className="font-bold">{row.rating?.toFixed(1)}</p>,
            },
        ],
        [t]
    )

    const fetchCustomerFeedbackReport = (page, filterData = filterObj, limit = perPage) => {
        const offset = (page - 1) * limit
        getCustomerFeedbackReport({
            variables: {
                page: {
                    limit,
                    offset,
                },
                feedbackFilter: {
                    travel_date: {
                        start_date:
                            filterData.travel_date.start_date || moment().subtract(6, 'months').format('YYYY-MM-DD'),
                        end_date: filterData.travel_date.end_date || moment().format('YYYY-MM-DD'),
                    },
                    station_ids: filterData.station_ids || null,
                    action_taken: filterData.action_taken,
                },
            },
        })
    }

    useEffect(() => {
        if (filterApplied) {
            fetchCustomerFeedbackReport(currentPage, filterObj, perPage)
        }
    }, [currentPage, perPage, filterApplied])

    const handlePageChange = page => {
        if (filterApplied) {
            setCurrentPage(page)
        }
    }

    const handlePerRowsChange = newPerPage => {
        if (newPerPage !== perPage && filterApplied) {
            setPerPage(newPerPage)
            setCurrentPage(1)
        }
    }

    const handleFilter = () => {
        setCurrentPage(1)
        setFilterApplied(true)
        fetchCustomerFeedbackReport(1, filterObj)
    }

    const handleClear = () => {
        setFilterObj(INITIAL_FILTER_STATE)
        setCurrentPage(1)
        setPerPage(10)
        setFilterApplied(false)
        setData([]) // Clear existing data
    }

    const fetchLocations = (searchString = '') => {
        searchLocation({
            variables: {
                page: {
                    offset: 0,
                    limit: 100,
                },
                searchString,
            },
        })
    }

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#f9fafb',
                color: '#111827',
                fontWeight: 600,
                fontSize: '0.875rem',
                paddingLeft: '0.75rem',
                paddingRight: '0.75rem',
                justifyContent: 'flex-start', // For proper RTL/LTR alignment
            },
        },
        cells: {
            style: {
                paddingLeft: '0.75rem',
                paddingRight: '0.75rem',
            },
        },
        table: {
            style: {
                tableLayout: 'fixed', // Make table respect column widths
            },
        },
    }

    useEffect(() => {
        fetchLocations()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Run once on mount - lazy queries are stable

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1]">
                            <ListBulletIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                        </div>
                        <div>
                            <span className="ms-3">{t('Customer Feedback Report')}</span>
                            <p className="ms-3 text-sm font-normal leading-6 text-gray-500">
                                {t('Summary of customer ratings and comments.')}
                            </p>
                        </div>
                    </h1>

                    {/* Filters */}
                    <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('Travel Date Range')}</label>
                            <Flatpickr
                                options={{
                                    mode: 'range',
                                    dateFormat: 'Y-m-d',
                                    defaultDate: [
                                        moment().subtract(6, 'months').format('YYYY-MM-DD'),
                                        moment().format('YYYY-MM-DD'),
                                    ],
                                }}
                                value={[filterObj.travel_date.start_date, filterObj.travel_date.end_date]}
                                onChange={dates => {
                                    setFilterObj({
                                        ...filterObj,
                                        travel_date: {
                                            start_date: dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : null,
                                            end_date: dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : null,
                                        },
                                    })
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Station')}</label>
                            <Select
                                value={filterObj.station_ids || ''}
                                onChange={e =>
                                    setFilterObj({
                                        ...filterObj,
                                        station_ids: e.target.value || null,
                                    })
                                }
                            >
                                <option value="">{t('All Stations')}</option>
                                {locations.map(location => (
                                    <option key={location.id} value={location.id}>
                                        {`${location.name.en} / ${location.name.ar}`}
                                    </option>
                                ))}
                            </Select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 pb-1">{t('Status')}</label>
                            <Select
                                value={filterObj.action_taken === null ? '' : filterObj.action_taken.toString()}
                                onChange={e => {
                                    const value = e.target.value
                                    setFilterObj({
                                        ...filterObj,
                                        action_taken: value === '' ? null : value === 'true',
                                    })
                                }}
                            >
                                <option value="">{t('All')}</option>
                                <option value="true">{t('Resolved')}</option>
                                <option value="false">{t('Pending')}</option>
                            </Select>
                        </div>

                        <div className="sm:col-span-1 flex items-end gap-2 mb-1">
                            <button
                                onClick={handleFilter}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Filter')}
                            </button>
                            <button
                                onClick={handleClear}
                                className="inline-flex items-center justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Divider className="my-4 mb-6" />

            {reportLoading ? (
                <div className="flex flex-col justify-center items-center bg-white h-96">
                    <img alt="loading" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center mb-4 -mt-3">
                        <p className="font-semibold">{t('Customer Feedback Reports')}</p>
                        {data.length > 0 && (
                            <button
                                onClick={handleExportCSV}
                                disabled={exportLoading}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {exportLoading ? (
                                    <>
                                        <ArrowPathIcon className="-ml-0.5 mr-1.5 h-5 w-5 animate-spin" />
                                        {t('Exporting...')}
                                    </>
                                ) : (
                                    <>
                                        <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                        {t('Export to CSV')}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    {!filterApplied || data.length === 0 ? (
                        <div className="text-center py-24 bg-gray-50 mt-0 rounded-lg w-full">
                            <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No records found!')}</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {t('Please add or modify your search criteria and try again.')}
                            </p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={data}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            onChangeRowsPerPage={handlePerRowsChange}
                            onChangePage={handlePageChange}
                            paginationDefaultPage={currentPage}
                            paginationRowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            customStyles={customStyles}
                            paginationPerPage={perPage}
                        />
                    )}
                </>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={dialogMessage}
                    onClose={() => setShowErrDialog(false)}
                />
            )}
        </>
    )
}
